import React, { useState, useContext, useRef, useEffect, useMemo } from 'react';
import { FaTimesCircle, FaTimes } from 'react-icons/fa';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import styles from './CheckDealTags.module.css';
import AuthContext from '../../context/AuthContext';
import LoadingOverlay from '../../components/UI/LoadingOverlay';
import QueueNotificationPopup from '../../components/UI/QueueNotificationPopup';
import TokenPurchaseModal from '../../components/UI/TokenPurchaseModal';
import StarIcon from '../../assets/dashboard_assets/star_yellow.png';
import step1Image from '../../assets/listing_assets/deal_tag_step1.png';
import step2Image from '../../assets/listing_assets/deal_tag_step2.png';
import step3Image from '../../assets/listing_assets/deal_tag_step3.png';

const CheckDealTags = () => {
    const authCtx = useContext(AuthContext);
    const location = useLocation();
    const [inputValue, setInputValue] = useState('');
    const [asinTags, setAsinTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [showOverflowModal, setShowOverflowModal] = useState(false);
    const [overflowAsins, setOverflowAsins] = useState([]);
    const [tokenBalance, setTokenBalance] = useState(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [taskId, setTaskId] = useState(null);
    const [isRequestInProgress, setIsRequestInProgress] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [historySearch, setHistorySearch] = useState('');
    const [historyPage, setHistoryPage] = useState(1);
    const [historyLimit, setHistoryLimit] = useState(10);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyTotalPages, setHistoryTotalPages] = useState(0);
    const [selectedHistoryBatchId, setSelectedHistoryBatchId] = useState(null);
    const [validationSearch, setValidationSearch] = useState('');
    const [productStatusFilter, setProductStatusFilter] = useState('all'); // 'all' | 'with_deals' | 'no_deals'
    const [validationDealType, setValidationDealType] = useState(''); // '' = all, or specific deal_badge string
    const [showTokenPurchaseModal, setShowTokenPurchaseModal] = useState(false);
    const [showZipcodeModal, setShowZipcodeModal] = useState(false);
    const [selectedZipcodes, setSelectedZipcodes] = useState([]);
    const [historySortOrder, setHistorySortOrder] = useState('newest'); // 'newest' | 'oldest'
    const [showHistorySortDropdown, setShowHistorySortDropdown] = useState(false);
    const [showHistoryFilters, setShowHistoryFilters] = useState(false);
    const actionsRef = useRef(null);
    const historySortRef = useRef(null);
    const historyFiltersRef = useRef(null);

    // Fetch current token balance
    const fetchTokenBalance = async () => {
        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_URL}/credits/tokens/balance`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setTokenBalance(data.tokens);
                return data.tokens; 
            } else {
                setTokenBalance(null);
                return null;
            }
        } catch (error) {
            setTokenBalance(null);
            return null;
        }
    };

    // Fetch deal checker history - fetch all tasks, then paginate flattened results client-side
    const fetchDealCheckerHistory = async () => {
        try {
            setHistoryLoading(true);
            setHistoryError('');
            
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            // Fetch a large number of tasks (100) to get all history, then paginate results client-side
            const response = await fetch(`${API_URL}/utility_dealtag/deal-checker-history?page=1&limit=100`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.tasks) {
                    // Flatten the tasks to get individual ASIN results
                    const flattenedResults = [];
                    data.tasks.forEach(task => {
                        const taskStatus = task.task_status || 'PENDING';
                        
                        // Check task status FIRST - prioritize status over results
                        if (taskStatus === 'PENDING') {
                            // Task created but Celery hasn't picked it up yet
                            task.asins.forEach(asin => {
                                flattenedResults.push({
                                    date: task.created_at,
                                    task_id: task.task_id,
                                    asin: asin,
                                    title: 'Pending...',
                                    deal_present: false,
                                    deal_badge: 'Pending',
                                    has_coupon: false,
                                    coupon_text: 'N/A',
                                    deal_details: null,
                                    current_price: null,
                                    original_price: null,
                                    savings_amount: null,
                                    discount_percentage: null,
                                    sold_by: null,
                                    delivery_info: null,
                                    zipcode: null,
                                    status: 'PENDING'
                                });
                            });
                        } else if (taskStatus === 'PROGRESS') {
                            // Task picked up by Celery and is being processed
                            task.asins.forEach(asin => {
                                flattenedResults.push({
                                    date: task.created_at,
                                    task_id: task.task_id,
                                    asin: asin,
                                    title: 'Processing...',
                                    deal_present: false,
                                    deal_badge: 'Processing',
                                    has_coupon: false,
                                    coupon_text: 'N/A',
                                    deal_details: null,
                                    current_price: null,
                                    original_price: null,
                                    savings_amount: null,
                                    discount_percentage: null,
                                    sold_by: null,
                                    delivery_info: null,
                                    zipcode: null,
                                    status: 'PROGRESS'
                                });
                            });
                        } else if (taskStatus === 'SUCCESS') {
                            // Task completed successfully - show results
                            if (task.result && task.result.success && task.result.results && task.result.results.length > 0) {
                                task.result.results.forEach(result => {
                                    flattenedResults.push({
                                        date: task.created_at,
                                        task_id: task.task_id,
                                        asin: result.asin,
                                        title: result.title || 'Not applicable',
                                        deal_present: result.has_deal || false,
                                        deal_badge: result.deal_badge || result.deal_type || 'Not applicable',
                                        deal_details: result,
                                        current_price: result.current_price || null,
                                        original_price: result.original_price || null,
                                        savings_amount: result.savings_amount || null,
                                        discount_percentage: result.discount_percentage || null,
                                        sold_by: result.sold_by || 'Not applicable',
                                        delivery_info: result.delivery_info || 'Not applicable',
                                        zipcode: result.zipcode ? String(result.zipcode) : null,
                                        main_image: result.main_image || '',
                                        has_coupon: result.has_coupon ?? false,
                                        coupon_text: result.coupon_text || 'N/A',
                                        status: 'SUCCESS'
                                    });
                                });
                            } else {
                                // SUCCESS status but no results - show ASINs with N/A
                                task.asins.forEach(asin => {
                                    flattenedResults.push({
                                        date: task.created_at,
                                        task_id: task.task_id,
                                        asin: asin,
                                        title: 'Not applicable',
                                        deal_present: false,
                                        deal_badge: 'Not applicable',
                                        has_coupon: false,
                                        coupon_text: 'N/A',
                                        deal_details: null,
                                        current_price: null,
                                        original_price: null,
                                        savings_amount: null,
                                        discount_percentage: null,
                                        sold_by: null,
                                        delivery_info: null,
                                        zipcode: null,
                                        status: 'SUCCESS'
                                    });
                                });
                            }
                        } else if (taskStatus === 'FAILURE') {
                            // Task failed
                            task.asins.forEach(asin => {
                                flattenedResults.push({
                                    date: task.created_at,
                                    task_id: task.task_id,
                                    asin: asin,
                                    title: 'Failed',
                                    deal_present: false,
                                    deal_badge: 'Failed',
                                    has_coupon: false,
                                    coupon_text: 'N/A',
                                    deal_details: null,
                                    current_price: null,
                                    original_price: null,
                                    savings_amount: null,
                                    discount_percentage: null,
                                    sold_by: null,
                                    delivery_info: null,
                                    zipcode: null,
                                    status: 'FAILURE'
                                });
                            });
                        } else {
                            // Unknown status - show ASINs with status
                            task.asins.forEach(asin => {
                                flattenedResults.push({
                                    date: task.created_at,
                                    task_id: task.task_id,
                                    asin: asin,
                                    title: 'Unknown status',
                                    deal_present: false,
                                    deal_badge: taskStatus,
                                    has_coupon: false,
                                    coupon_text: 'N/A',
                                    deal_details: null,
                                    current_price: null,
                                    original_price: null,
                                    savings_amount: null,
                                    discount_percentage: null,
                                    sold_by: null,
                                    delivery_info: null,
                                    zipcode: null,
                                    status: taskStatus
                                });
                            });
                        }
                    });
                    
                    // Sort by date (newest first)
                    flattenedResults.sort((a, b) => new Date(b.date) - new Date(a.date));
                    setHistoryData(flattenedResults);
                } else {
                    setHistoryData([]);
                }
            } else {
                setHistoryError('Failed to fetch history data');
            }
        } catch (error) {
            setHistoryError('Error loading history data');
        } finally {
            setHistoryLoading(false);
        }
    };

    // Handle date filtering
    const handleDateFilter = (date) => {
        setDateFilter(date);
        setHistoryPage(1); // Reset to first page when filter changes
    };

    // Get filtered history data (without pagination)
    const getFilteredHistoryDataAll = () => {
        let data = historyData;

        if (dateFilter) {
            const filterDate = new Date(dateFilter);
            data = data.filter(item => {
                const itemDate = new Date(item.date);
                return itemDate.toDateString() === filterDate.toDateString();
            });
        }

        if (historySearch.trim()) {
            const q = historySearch.trim().toUpperCase();
            data = data.filter(item => String(item.asin).toUpperCase().includes(q));
        }

        return data;
    };

    // How many thumbnails to show and grid layout (same as listing score batch cards): 2→2, 3→3, 4→4, 5→4, 6→6, 7→6, 8+→8. Grid cols: 1→1, 2→2, 3→3, 4→2, 6→3, 8→4.
    const getThumbnailDisplay = (count) => {
        const displayCount = count <= 4 ? count : count === 5 ? 4 : count <= 7 ? 6 : 8;
        const gridCols = displayCount <= 1 ? 1 : displayCount <= 3 ? displayCount : displayCount === 4 ? 2 : displayCount === 6 ? 3 : 4;
        return { displayCount, gridCols };
    };

    // Group filtered results by task_id (batch) for batch cards - like bulk listing score
    const getFilteredBatches = () => {
        const data = getFilteredHistoryDataAll();
        const byTask = {};
        for (const item of data) {
            const tid = item.task_id || 'unknown';
            if (!byTask[tid]) {
                byTask[tid] = { task_id: tid, date: item.date, results: [] };
            }
            byTask[tid].results.push(item);
            if (item.date && (!byTask[tid].date || new Date(item.date) > new Date(byTask[tid].date))) {
                byTask[tid].date = item.date;
            }
        }
        return Object.values(byTask).sort((a, b) =>
            historySortOrder === 'oldest'
                ? new Date(a.date) - new Date(b.date)
                : new Date(b.date) - new Date(a.date)
        );
    };

    const formatBatchDate = (d) => {
        if (!d) return '';
        const date = new Date(d);
        return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const formatBatchDateLong = (d) => {
        if (!d) return '';
        const date = new Date(d);
        const dateStr = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
        const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
        return `${dateStr} | ${timeStr}`;
    };

    // Extract percentage from coupon_text (e.g. "Apply 2% coupon" -> "2%")
    const formatCouponText = (text) => {
        if (!text || text === 'N/A' || text === 'Not applicable') return '-';
        const m = String(text).match(/\d+%?/);
        return m ? (m[0].endsWith('%') ? m[0] : m[0] + '%') : text;
    };
    // Thumbnail URL for ASIN (placeholder; can be replaced with real image API)
    const getAsinThumbnailUrl = (asin) => {
        if (!asin) return 'https://placehold.co/43x42?text=ASIN';
        return `https://placehold.co/43x42?text=${encodeURIComponent(asin.slice(-4))}`;
    };

    // Group selected batch results by ASIN for Deal Tag Validation detail view. Applies search and product status filter.
    const validationGroupedByAsin = useMemo(() => {
        if (!selectedHistoryBatchId) return [];
        const batch = getFilteredBatches().find(b => b.task_id === selectedHistoryBatchId);
        if (!batch || !batch.results.length) return [];
        const q = (validationSearch || '').toLowerCase().trim();
        let results = batch.results;
        if (q) {
            results = results.filter(r =>
                (r.asin && r.asin.toLowerCase().includes(q)) ||
                (r.title && r.title.toLowerCase().includes(q))
            );
        }
        if (productStatusFilter === 'with_deals') results = results.filter(r => r.deal_present);
        else if (productStatusFilter === 'no_deals') results = results.filter(r => !r.deal_present);
        if (validationDealType) {
            results = results.filter(r => r.deal_badge && String(r.deal_badge).trim() === validationDealType);
        }
        const byAsin = {};
        results.forEach(r => {
            const key = r.asin || 'unknown';
            if (!byAsin[key]) {
                byAsin[key] = {
                    asin: key,
                    title: r.title || 'N/A',
                    // Prefer the real product image (if available), otherwise fall back to the ASIN-based placeholder
                    imageUrl: r.main_image || getAsinThumbnailUrl(r.asin),
                    rows: [],
                };
            }
            const deliveryInfo = r.delivery_info && r.delivery_info !== 'N/A' && r.delivery_info !== 'Not applicable' ? r.delivery_info : null;
            const deliveryParts = deliveryInfo ? deliveryInfo.split(/\s+or\s+/i) : [];
            byAsin[key].rows.push({
                zipcode: r.zipcode ? String(r.zipcode) : '-',
                deal_badge: r.deal_badge,
                deal_present: r.deal_present,
                sold_by: r.sold_by,
                delivery_info: r.delivery_info,
                standardDelivery: deliveryParts[0]?.trim() || deliveryInfo,
                primeDelivery: deliveryParts[1]?.trim() || null,
                has_coupon: r.has_coupon ?? false,
                coupon_text: r.coupon_text || 'N/A',
                current_price: r.current_price || 'N/A',
            });
        });
        return Object.values(byAsin);
    }, [selectedHistoryBatchId, validationSearch, productStatusFilter, validationDealType, historyData]);

    // Unique deal types in selected batch (for Deal type dropdown)
    const validationDealTypeOptions = useMemo(() => {
        if (!selectedHistoryBatchId) return [];
        const batch = getFilteredBatches().find(b => b.task_id === selectedHistoryBatchId);
        if (!batch) return [];
        const set = new Set();
        batch.results.forEach(r => {
            if (r.deal_present && r.deal_badge && r.deal_badge !== 'N/A' && r.deal_badge !== 'Not applicable' && r.deal_badge !== 'Not Applicable') {
                set.add(String(r.deal_badge).trim());
            }
        });
        return Array.from(set).sort();
    }, [selectedHistoryBatchId, historyData]);

    const validationTotalActiveDeals = useMemo(() => {
        if (!selectedHistoryBatchId) return 0;
        const batch = getFilteredBatches().find(b => b.task_id === selectedHistoryBatchId);
        return batch ? batch.results.filter(r => r.deal_present).length : 0;
    }, [selectedHistoryBatchId, historyData]);

    const renderDealHistoryRow = (item, index) => {
        const renderDeliveryText = (text) => {
            if (!text) return '-';
            const match = text.match(/^(.*?)(Order within[^.]*\.)/i);
            if (match) {
                return (
                    <>
                        {match[1]}
                        <span style={{ fontSize: '0.8em', color: '#0066cc', fontWeight: '600' }}>{match[2]}</span>
                    </>
                );
            }
            return text;
        };
        const deliveryInfo = item.delivery_info && item.delivery_info !== 'N/A' && item.delivery_info !== 'Not applicable' ? item.delivery_info : null;
        const deliveryParts = deliveryInfo ? deliveryInfo.split(/\s+or\s+/i) : [];
        const standardDelivery = deliveryParts[0]?.trim() || (deliveryInfo ? deliveryInfo : null);
        const primeDelivery = deliveryParts[1]?.trim() || null;
        return (
            <tr key={`${item.task_id}-${item.asin}-${item.zipcode || 'no-zip'}-${index}`}>
                <td>{new Date(item.date).toLocaleDateString()}</td>
                <td className={styles.asinCell}>{item.asin}</td>
                <td>{item.title}</td>
                <td className={styles.statusCell}>
                    <span className={styles.statusBadge}>
                        {item.status === 'PENDING' ? (<> Pending </>) : item.status === 'PROGRESS' ? (<> Processing </>) : item.status === 'FAILURE' ? (<> <FaTimesCircle className={styles.noDeal} /> Failed </>) : item.deal_present ? (<> <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="5" cy="5" r="5" fill="#009F38"/></svg> Yes </>) : (<> <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="5" cy="5" r="5" fill="#F84A4A"/></svg> No </>)}
                    </span>
                </td>
                <td className={styles.dealBadgeCell}>
                    {item.status === 'PENDING' || item.status === 'PROGRESS' ? <span className={styles.dealTagEmpty}>{item.deal_badge || '-'}</span> : item.status === 'FAILURE' ? <span className={styles.dealTagEmpty}>Failed</span> : item.deal_badge && item.deal_badge !== '-' && item.deal_badge !== 'N/A' && item.deal_badge !== 'Processing' && item.deal_badge !== 'Pending' ? <span className={styles.dealTagBadge}>{item.deal_badge}</span> : item.deal_badge === 'N/A' || item.deal_badge === 'Not Applicable' || item.deal_badge === 'Not applicable' ? <span className={styles.dealTagNA}>Not applicable</span> : <span className={styles.dealTagEmpty}>{item.deal_badge || '-'}</span>}
                </td>
                <td>{item.sold_by && item.sold_by !== 'N/A' && item.sold_by !== 'Not applicable' ? item.sold_by : '-'}</td>
                <td>{standardDelivery ? renderDeliveryText(standardDelivery) : '-'}</td>
                <td>{primeDelivery ? renderDeliveryText(primeDelivery) : '-'}</td>
                <td>{item.zipcode ? String(item.zipcode) : '-'}</td>
            </tr>
        );
    };

    // Get filtered and paginated history data
    const getFilteredHistoryData = () => {
        const allData = getFilteredHistoryDataAll();
        
        // Apply pagination
        const startIndex = (historyPage - 1) * historyLimit;
        const endIndex = startIndex + historyLimit;
        
        return allData.slice(startIndex, endIndex);
    };

    // Get total count of filtered results
    const getFilteredHistoryTotal = () => {
        return getFilteredHistoryDataAll().length;
    };

    // Get total pages for filtered results
    const getFilteredHistoryTotalPages = () => {
        const total = getFilteredHistoryTotal();
        return Math.ceil(total / historyLimit) || 1;
    };

    // Map a single result item to Excel row (shared by history and validation batch export)
    const mapItemToExcelRow = (item) => {
        const dealType = item.deal_details?.deal_type ||
            (item.deal_badge && item.deal_badge !== 'Not applicable' && item.deal_badge !== 'Pending' && item.deal_badge !== 'Processing' && item.deal_badge !== 'Failed' ? item.deal_badge : '-');
        const deliveryInfo = item.delivery_info && item.delivery_info !== 'N/A' && item.delivery_info !== 'Not applicable' ? item.delivery_info : null;
        const deliveryParts = deliveryInfo ? deliveryInfo.split(/\s+or\s+/i) : [];
        const nonPrimeDelivery = deliveryParts[0]?.trim() || (deliveryInfo || null);
        const primeDelivery = deliveryParts[1]?.trim() || null;
        const parsePriceToNumber = (value) => {
            if (!value || typeof value !== 'string') return null;
            const cleaned = value.replace(/[^\d.,]/g, '').replace(/,/g, '');
            const num = Number(cleaned);
            return Number.isFinite(num) ? num : null;
        };
        const normalizeDiscount = (value) => {
            if (value == null) return null;
            const s = String(value).trim();
            if (!s || s === '-' || s.toLowerCase() === 'n/a' || s.toLowerCase() === 'not applicable') return null;
            if (s.includes('%')) return s;
            const num = Number(s.replace(/[^\d.]/g, ''));
            return Number.isFinite(num) ? `${Math.round(num)}%` : s;
        };
        const discountFromBackend =
            normalizeDiscount(item.discount_percentage) ||
            normalizeDiscount(item.deal_details?.discount_percentage) ||
            normalizeDiscount(item.deal_details?.discount_percent) ||
            normalizeDiscount(item.deal_details?.discount);
        const mrpNum = parsePriceToNumber(item.original_price);
        const dealNum = parsePriceToNumber(item.current_price);
        const computedDiscount = mrpNum && dealNum && mrpNum > 0 && mrpNum >= dealNum ? `${Math.round(((mrpNum - dealNum) / mrpNum) * 100)}%` : null;
        return {
            'Date': new Date(item.date).toLocaleDateString(),
            'ASIN': item.asin || '-',
            'Title': item.title && item.title !== 'Pending...' && item.title !== 'Processing...' ? item.title : '-',
            'Deal Present': item.deal_present ? 'Yes' : 'No',
            'Deal Badge': item.deal_badge && item.deal_badge !== 'Not applicable' && item.deal_badge !== 'Pending' && item.deal_badge !== 'Processing' && item.deal_badge !== 'Failed' ? item.deal_badge : '-',
            'Deal Type': dealType,
            'Current Price/Deal Price': item.current_price && item.current_price !== 'N/A' ? item.current_price : '-',
            'MRP': item.original_price && item.original_price !== 'N/A' ? item.original_price : '-',
            'Savings Amount': item.savings_amount && item.savings_amount !== 'N/A' ? item.savings_amount : '-',
            'Discount Percentage': discountFromBackend || computedDiscount || '-',
            'Buybox Winner': item.sold_by && item.sold_by !== 'N/A' && item.sold_by !== 'Not applicable' ? item.sold_by : '-',
            'Coupon': (item.has_coupon && (item.coupon_text || item.deal_details?.coupon_text) && String(item.coupon_text || item.deal_details?.coupon_text || '').trim() !== 'N/A') ? formatCouponText(item.coupon_text || item.deal_details?.coupon_text) : 'Not Applicable',
            'Prime Delivery': primeDelivery || '-',
            'Non Prime Delivery': nonPrimeDelivery || '-',
            'Pincode': item.zipcode || '-',
            'Status': item.status || '-'
        };
    };

    const buildDealCheckWorkbook = (excelData) => {
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Deal Check Results');
        const columnWidths = [
            { wch: 12 }, { wch: 15 }, { wch: 50 }, { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 45 }, { wch: 60 }, { wch: 10 }, { wch: 12 }
        ];
        worksheet['!cols'] = columnWidths;
        return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    };

    // Export all filtered results to Excel
    const handleDownloadExcel = () => {
        const allData = getFilteredHistoryDataAll();
        const successData = allData.filter(item => item.status === 'SUCCESS');
        if (successData.length === 0) {
            alert('No successful results to export');
            return;
        }
        const excelData = successData.map(mapItemToExcelRow);
        const excelBuffer = buildDealCheckWorkbook(excelData);
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        let filename = 'deal_check_results_success';
        if (dateFilter) filename += `_${new Date(dateFilter).toISOString().slice(0, 10).replace(/-/g, '')}`;
        if (historySearch.trim()) filename += '_filtered';
        filename += `_${timestamp}.xlsx`;
        saveAs(blob, filename);
    };

    // Export only the selected batch (Deal Tag Validation page)
    const handleDownloadValidationBatchExcel = () => {
        const batch = getFilteredBatches().find(b => b.task_id === selectedHistoryBatchId);
        if (!batch || !batch.results.length) {
            alert('No batch data to export');
            return;
        }
        const successData = batch.results.filter(item => item.status === 'SUCCESS');
        if (successData.length === 0) {
            alert('No successful results in this batch to export');
            return;
        }
        const excelData = successData.map(mapItemToExcelRow);
        const excelBuffer = buildDealCheckWorkbook(excelData);
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        const batchDate = batch.date ? new Date(batch.date).toISOString().slice(0, 10).replace(/-/g, '') : '';
        const filename = `deal_validation_batch_${batch.task_id || batchDate}_${timestamp}.xlsx`;
        saveAs(blob, filename);
    };

    // Fetch token balance on component mount
    useEffect(() => {
        if (authCtx.token) {
            fetchTokenBalance();
        }
    }, [authCtx.token]);

    // Check for success messages from token purchase redirect
    useEffect(() => {
        if (location.state?.message && location.state?.type === 'success') {
            // Show success message temporarily
            setError('');
            // You could add a success state here or just refresh token balance
            if (authCtx.token) {
                fetchTokenBalance();
            }

            // Clear the location state to prevent showing message on refresh
            window.history.replaceState({}, document.title);
        }
    }, [location.state, authCtx.token]);

    // Open history when navigated from Manage page (Deal Tag button)
    useEffect(() => {
        if (location.state?.openHistory) {
            setShowHistory(true);
            setHistoryPage(1);
            fetchDealCheckerHistory();
            window.history.replaceState({}, document.title, location.pathname);
        }
    }, [location.state?.openHistory]);

    // Close sort dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (historySortRef.current && !historySortRef.current.contains(e.target)) {
                setShowHistorySortDropdown(false);
            }
        };
        if (showHistorySortDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showHistorySortDropdown]);

    // Close filters dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (historyFiltersRef.current && !historyFiltersRef.current.contains(e.target)) {
                setShowHistoryFilters(false);
            }
        };
        if (showHistoryFilters) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showHistoryFilters]);

    const handleInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addAsinTag();
        }
    };

    const addAsinTag = () => {
        const trimmedInput = inputValue.trim();
        if (!trimmedInput) {
            setError('Please enter a valid ASIN');
            return;
        }

        // Split by commas, line breaks, or whitespace and process multiple ASINs
        const asins = trimmedInput.split(/[\s,\n\r]+/).map(asin => asin.trim().toUpperCase()).filter(asin => asin.length === 10);

        if (asins.length === 0) {
            setError('Please enter at least one valid ASIN (10 characters)');
            return;
        }

        // Check for 100 ASIN limit
        const uniqueAsins = [...new Set([...asinTags, ...asins])];
        
        if (uniqueAsins.length > 100) {
            const acceptedAsins = uniqueAsins.slice(0, 100);
            const rejectedAsins = uniqueAsins.slice(100);

            setAsinTags(acceptedAsins);
            setOverflowAsins(rejectedAsins);
            setShowOverflowModal(true);
            setInputValue('');
            setError('');

            // Scroll to actions after a short delay to allow DOM update
            setTimeout(() => {
                if (actionsRef.current) {
                    actionsRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);
            return;
        }

        setAsinTags(uniqueAsins);
        setInputValue('');
        setError('');

        // Scroll to actions after a short delay to allow DOM update
        setTimeout(() => {
            if (actionsRef.current) {
                actionsRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }, 100);
    };

    const removeAsinTag = (asinToRemove) => {
        setAsinTags(asinTags.filter(asin => asin !== asinToRemove));
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = (e.clipboardData || window.clipboardData).getData('text');
        const asins = pastedText.split(/[\s,\n\r]+/).map(asin => asin.trim().toUpperCase()).filter(asin => asin.length === 10);

        const uniqueAsins = [...new Set([...asinTags, ...asins])];

        if (uniqueAsins.length > 100) {
            const acceptedAsins = uniqueAsins.slice(0, 100);
            const rejectedAsins = uniqueAsins.slice(100);

            setAsinTags(acceptedAsins);
            setOverflowAsins(rejectedAsins);
            setShowOverflowModal(true);
            setInputValue('');
            setError('');

            // Scroll to actions after a short delay to allow DOM update
            setTimeout(() => {
                if (actionsRef.current) {
                    actionsRef.current.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }
            }, 100);
            return;
        }

        setAsinTags(uniqueAsins);
        setInputValue('');
        setError('');

        // Scroll to actions after a short delay to allow DOM update
        setTimeout(() => {
            if (actionsRef.current) {
                actionsRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }, 100);
    };

    // Calculate total tokens needed (ASINs * zipcodes)
    const calculateRequiredTokens = () => {
        const asinCount = asinTags.length;
        if (asinCount === 0) return 0;
        
        // If no zipcodes selected OR only default (560001) selected, use default behavior
        if (selectedZipcodes.length === 0 || 
            (selectedZipcodes.length === 1 && selectedZipcodes[0] === '560001')) {
            return asinCount;
        }
        
        // If multiple zipcodes OR one non-default zipcode, multiply ASINs by zipcodes
        return asinCount * selectedZipcodes.length;
    };

    // Validate token balance before proceeding
    const validateTokenBalance = async () => {
        try {
            const currentBalance = await fetchTokenBalance();
            
            if (currentBalance === null) {
                setError('Unable to verify credit balance. Please try again.');
                return false;
            }
            
            const requiredTokens = calculateRequiredTokens();
            if (currentBalance < requiredTokens) {
                setError(`Insufficient credits. Required: ${requiredTokens}, Available: ${currentBalance}`);
                return false;
            }
            
            return true;
        } catch (error) {
            setError('Unable to verify credit balance. Please try again.');
            return false;
        }
    };

    const handleCheckInstantly = async () => {
        if (asinTags.length === 0) {
            setError('Please add at least one ASIN');
            return;
        }

        // Prevent multiple simultaneous requests
        if (isRequestInProgress) {
            setError('A request is already in progress. Please wait.');
            return;
        }

        if (!(await validateTokenBalance())) {
            return;
        }

        setIsRequestInProgress(true);
        setLoading(true);
        setError('');

        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            
            // Use the unified API endpoint - it handles both single and multiple ASINs
            const requestBody = asinTags.length === 1 
                ? { asin: asinTags[0] } 
                : { asins: asinTags };
            
            // Add zipcodes if multiple selected (more than just Bangalore)
            if (selectedZipcodes.length > 1 || (selectedZipcodes.length === 1 && selectedZipcodes[0] !== '560001')) {
                requestBody.zipcodes = selectedZipcodes;
            }

            const response = await fetch(`${API_URL}/utility_dealtag/check-deal-tags`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();


            if (!response.ok) {
                // Handle specific errors
                if (response.status === 402) {
                    setError(data.message || 'Insufficient credits for this request');
                } else if (response.status === 409) {
                    // Duplicate request error
                    setError(data.message || 'A similar request is already being processed. Please wait for it to complete.');
                } else {
                    throw new Error(data.error || 'Failed to check deal tags');
                }
                return;
            }

            // Check if it's an async response (202 status)
            if (response.status === 202) {
                // Async processing - show success popup
                setTaskId(data.task_id);
                setShowSuccessPopup(true);
                setSelectedZipcodes([]); // Reset selected zipcodes
                setShowZipcodeModal(false); // Close zipcode modal if open

                // Live-update credits the moment the task is queued (backend already
                // debited atomically). Avoids users needing to refresh to see the
                // new balance in the navbar / on this page.
                if (data.credits && typeof data.credits.remaining === 'number') {
                    if (authCtx.updateUserCredit) {
                        authCtx.updateUserCredit(data.credits.remaining);
                    }
                    if (authCtx.updateUserTokens) {
                        authCtx.updateUserTokens(data.credits.remaining);
                    }
                    setTokenBalance(data.credits.remaining);
                }

                // Start polling for results
                pollTaskStatus(data.task_id);
            } else {
                // Sync processing - reset form after successful processing
                // Refresh token balance after successful operation
                await fetchTokenBalance();

                // Update AuthContext tokens if available
                if (authCtx.updateUserTokens && tokenBalance !== null) {
                    const tokensConsumed = data.tokens_consumed || asinTags.length;
                    const newBalance = Math.max(0, tokenBalance - tokensConsumed);
                    authCtx.updateUserTokens(newBalance);
                }
                
                // Reset zipcodes and close modal
                setSelectedZipcodes([]);
                setShowZipcodeModal(false);
                
                // Reset form after successful processing
                resetFormFields();
            }

        } catch (err) {
            setError(err.message || 'Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
            setIsRequestInProgress(false);
        }
    };

    // Poll task status for async results
    const pollTaskStatus = async (taskId) => {
        const maxAttempts = 60; // Poll for up to 5 minutes (5 seconds * 60)
        let attempts = 0;

        const poll = async () => {
            try {
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                const response = await fetch(`${API_URL}/utility_dealtag/task-status/${taskId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${authCtx.token}`,
                        'Content-Type': 'application/json'
                    }
                });

                const data = await response.json();

                if (data.status === 'SUCCESS') {
                    // Refresh token balance after successful operation
                    await fetchTokenBalance();

                    // Update AuthContext tokens if available
                    if (authCtx.updateUserTokens && tokenBalance !== null) {
                        const tokensConsumed = data.result.tokens_consumed || asinTags.length;
                        const newBalance = Math.max(0, tokenBalance - tokensConsumed);
                        authCtx.updateUserTokens(newBalance);
                    }
                    
                    setShowSuccessPopup(false);
                    // Stay on the current view. If user is on History, refresh it in-place.
                    if (showHistory) {
                        fetchDealCheckerHistory();
                    }
                    setTaskId(null);
                    return;
                } else if (data.status === 'FAILURE') {
                    setError(data.error || 'Task failed to complete');
                    setShowSuccessPopup(false);
                    return;
                } else if (data.status === 'PROGRESS') {
                    // Continue polling
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(poll, 5000); // Poll every 5 seconds
                    } else {
                        setError('Task is taking longer than expected. Please check back later.');
                        setShowSuccessPopup(false);
                    }
                }
            } catch (err) {
                setError('Failed to check task status');
                setShowSuccessPopup(false);
            }
        };

        // Start polling after a short delay
        setTimeout(poll, 2000);
    };

    const handleViewHistory = () => {
        setShowHistory(true);
        setHistoryPage(1); // Reset to first page when opening
        fetchDealCheckerHistory(); // Fetch history when opening
    };

    const handlePageChange = (newPage) => {
        const totalPages = getFilteredHistoryTotalPages();
        if (newPage >= 1 && newPage <= totalPages) {
            setHistoryPage(newPage);
            // Scroll to top of table when page changes
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleLimitChange = (newLimit) => {
        setHistoryLimit(newLimit);
        setHistoryPage(1); // Reset to first page when changing limit
        // Scroll to top of table when limit changes
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleBackFromHistory = () => {
        setShowHistory(false);
        setSelectedHistoryBatchId(null);
    };

    // Reset input fields without forcing a view change (e.g., don't auto-exit History)
    const resetFormFields = () => {
        setInputValue('');
        setAsinTags([]);
        setError('');
        setShowSuccessPopup(false);
        setTaskId(null);
    };

    const handleReset = () => {
        setInputValue('');
        setAsinTags([]);
        setError('');
        setShowHistory(false);
        setShowSuccessPopup(false);
        setTaskId(null);
    };

    const handleCloseSuccessPopup = () => {
        setShowSuccessPopup(false);
    };
    const handleCloseTokenPurchaseModal = () => {
        setShowTokenPurchaseModal(false);
    };



    return (
        <div className={`${styles.container} ${showHistory ? styles.historyView : ''}`}>

            {!showHistory && (
                <div className={styles.newSearchPage}>
                    {/* Title */}
                    <div className={styles.newSearchTitleContainer}>
                        <div className={styles.newSearchTitle}>Check Deal Tags for Amazon</div>
                        <div className={styles.newSearchTitleLine}></div>
                    </div>

                    {/* Search Form */}
                    <div className={styles.newSearchFormContainer}>
                        <form onSubmit={(e) => { e.preventDefault(); addAsinTag(); }} className={styles.newSearchForm}>
                            {/* Input Field */}
                            <div className={styles.newSearchInputContainer}>
                                <div className={styles.newSearchInputCard}>
                                    <div className={styles.newSearchInputWrapper}>
                                        {/* ASIN Chips */}
                                        {asinTags.map((asin) => (
                                            <div key={asin} className={styles.asinChip}>
                                                <span className={styles.asinChipText}>{asin}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeAsinTag(asin)}
                                                    className={styles.asinChipRemove}
                                                    disabled={loading}
                                                    aria-label={`Remove ${asin}`}
                                                >
                                                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8 0.7L7.3 0L4 3.3L0.7 0L0 0.7L3.3 4L0 7.3L0.7 8L4 4.7L7.3 8L8 7.3L4.7 4L8 0.7Z" fill="#505050"/>
</svg>
                                                </button>
                                            </div>
                                        ))}
                                        <input
                                            type="text"
                                            value={inputValue}
                                            onChange={(e) => setInputValue(e.target.value)}
                                            onKeyPress={handleInputKeyPress}
                                            onPaste={handlePaste}
                                            placeholder={asinTags.length === 0 ? "Enter ASIN(s) and press Enter — you can paste a single ASIN, multiple ASINs, or an entire column" : ""}
                                            className={styles.newSearchInput}
                                            disabled={loading}
                                            maxLength={10}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className={styles.dealTagActionsContainer}>
                                <button
                                    onClick={() => setShowZipcodeModal(true)}
                                    className={styles.historyButton}
                                    disabled={loading || isRequestInProgress}
                                    type="button"
                                >
                                    <span>Zipcode</span>
                                </button>
                                <button
                                    onClick={handleCheckInstantly}
                                    className={styles.newAnalyseButton}
                                    disabled={loading || isRequestInProgress || asinTags.length === 0}
                                    type="button"
                                >
                                    {loading ? (
                                        <>
                                            <span className={styles.analyseButtonText}>Checking...</span>
                                        </>
                                    ) : (
                                        <>
                                            <img src={StarIcon} alt="Star" className={styles.starIcon} />
                                            {asinTags.length > 0 && (
                                                <span className={styles.asinCountBadge}>{calculateRequiredTokens()}</span>
                                            )}
                                            <span className={styles.analyseButtonText}>Check Instantly</span>
                                        </>
                                    )}
                                </button>
                                <button
                                    onClick={handleViewHistory}
                                    className={styles.historyButton}
                                    disabled={loading || isRequestInProgress}
                                    type="button"
                                >
                                    <span>History</span>
                                </button>
                            </div>
                        </form>
                    </div>

                    {error && (
                        <div className={styles.errorMessage}>
                            <div className={styles.errorContent}>
                                <p>{error}</p>
                                {(error.includes('Insufficient credits') || error.includes('Unable to verify credit balance')) && (
                                    <button 
                                        className={styles.buyTokensButton}
                                        onClick={() => setShowTokenPurchaseModal(true)}
                                    >
                                        Buy Credits
                                    </button>
                                )}
                            </div>
                        </div>
                    )}

                    {/* How it works Section */}
                    <div className={styles.newHowItWorksSection}>
                        <div className={styles.newHowItWorksHeader}>
                            <div className={styles.howItWorksBadge}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g opacity="0.7">
                                        <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="url(#paint0_linear_361_1889)"/>
                                        <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="url(#paint1_linear_361_1889)"/>
                                    </g>
                                    <defs>
                                        <linearGradient id="paint0_linear_361_1889" x1="-4.47035e-08" y1="10" x2="11.0143" y2="16" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#2766AA"/>
                                            <stop offset="1" stopColor="#0752B0"/>
                                        </linearGradient>
                                        <linearGradient id="paint1_linear_361_1889" x1="9" y1="3.5" x2="15.425" y2="7" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#2766AA"/>
                                            <stop offset="1" stopColor="#0752B0"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <span>How it works</span>
                            </div>
                        </div>

                        <div className={styles.newHowItWorksSteps}>
                            {/* Step 1 */}
                            <div className={styles.newHowItWorksStep}>
                                <div className={styles.newStepImageContainer}>
                                    <img 
                                        src={step1Image} 
                                        alt="Step 1: Pickup the ASIN from the Product URL" 
                                        className={styles.newStepImage}
                                    />
                                </div>
                                <div className={styles.newStepText}>1. Pickup the ASIN from the Product URL</div>
                            </div>

                            {/* Step 2 */}
                            <div className={styles.newHowItWorksStep}>
                                <div className={styles.newStepImageContainer}>
                                    <img 
                                        src={step2Image} 
                                        alt="Step 2: Paste it on the box above" 
                                        className={styles.newStepImage}
                                    />
                                </div>
                                <div className={styles.newStepText}>2. Paste it on the box above</div>
                            </div>

                            {/* Step 3 */}
                            <div className={styles.newHowItWorksStep}>
                                <div className={styles.newStepImageContainer}>
                                    <img 
                                        src={step3Image} 
                                        alt="Step 3: Click on Analyse...and done" 
                                        className={styles.newStepImage}
                                    />
                                </div>
                                <div className={styles.newStepText}>3. Click on Analyse...and done</div>
                            </div>
                        </div>
                    </div>
                </div>
            )}


            {/* History Section - header styled like Manage page */}
            {showHistory && (
                <div className={styles.historyContainer}>
                    {!selectedHistoryBatchId && (
                    <div className={styles.historyHeaderContainer}>
                        <div className={styles.historyHeaderTitleSection}>
                            <div className={styles.historyHeaderTitleRow}>
                                <button
                                    type="button"
                                    onClick={handleBackFromHistory}
                                    aria-label="Back"
                                >
                                <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M25 13.75H9.7875L16.775 6.7625L15 5L5 15L15 25L16.7625 23.2375L9.7875 16.25H25V13.75Z" fill="#505050"/>
                                </svg>
                                </button>
                                <h1 className={styles.historyHeaderTitle}>Deal Tag Check History</h1>
                            </div>
                            <div className={styles.historyHeaderInfoBox}>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g opacity="0.7">
                                        <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="url(#paint0_linear_history)"/>
                                        <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="url(#paint1_linear_history)"/>
                                    </g>
                                    <defs>
                                        <linearGradient id="paint0_linear_history" x1="-4.47035e-08" y1="10" x2="11.0143" y2="16" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#2766AA"/>
                                            <stop offset="1" stopColor="#0752B0"/>
                                        </linearGradient>
                                        <linearGradient id="paint1_linear_history" x1="9" y1="3.5" x2="15.425" y2="7" gradientUnits="userSpaceOnUse">
                                            <stop stopColor="#2766AA"/>
                                            <stop offset="1" stopColor="#0752B0"/>
                                        </linearGradient>
                                    </defs>
                                </svg>
                                <span className={styles.historyHeaderInfoBoxText}>Organize, search, and manage all your product listings in one place</span>
                            </div>
                        </div>

                        <div className={styles.historyHeaderRow}>
                            <div className={styles.historySearchBarBox}>
                                <div className={styles.historySearchBarInner}>
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10.421 0C14.5954 0 18 3.40462 18 7.57899C18 11.7534 14.5954 15.158 10.421 15.158C8.67422 15.158 7.06155 14.5613 5.77761 13.5621L1.61719 17.7224C1.24712 18.0925 0.647591 18.0925 0.277544 17.7224C-0.0925028 17.3524 -0.0925265 16.7519 0.277544 16.3828L4.43796 12.2234C3.43876 10.9383 2.84203 9.32678 2.84203 7.58006C2.84203 3.40569 6.24668 0.00106589 10.4211 0.00106589L10.421 0ZM10.421 1.89475C7.27068 1.89475 4.73667 4.42874 4.73667 7.57899C4.73667 10.7292 7.27068 13.2632 10.421 13.2632C13.5712 13.2632 16.1052 10.7292 16.1052 7.57899C16.1052 4.42874 13.5712 1.89475 10.421 1.89475Z" fill="#D2D2D2"/>
                                    </svg>
                                    <input
                                        id="historySearch"
                                        type="text"
                                        className={`${styles.historySearchBarInput} ${historySearch ? styles.historySearchBarInputFilled : ''}`}
                                        value={historySearch}
                                        onChange={(e) => {
                                            setHistorySearch(e.target.value);
                                            setHistoryPage(1);
                                        }}
                                        placeholder="Search for ASIN and SKU-IDs"
                                    />
                                    {historySearch && (
                                        <button type="button" onClick={() => setHistorySearch('')} className={styles.historyClearSearchBtn} aria-label="Clear search">
                                            <FaTimes className={styles.historyClearSearchBtnIcon} />
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div className={styles.historyFiltersWrap} ref={historyFiltersRef}>
                                <button
                                    type="button"
                                    className={styles.historyFiltersBtn}
                                    onClick={() => setShowHistoryFilters((p) => !p)}
                                    aria-expanded={showHistoryFilters}
                                >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M13.6431 0H2.35692C1.05846 0 0 1.05846 0 2.35692C0 2.98462 0.246154 3.57538 0.689231 4.02462L5.53846 8.87385V14.5169C5.53846 15.3354 6.20308 16 7.02154 16C7.41538 16 7.79692 15.84 8.07385 15.5631L9.92 13.7169C10.2708 13.3662 10.4615 12.9046 10.4615 12.4123V8.86769L15.3108 4.01846C15.7538 3.57538 16 2.98462 16 2.35692C16 1.05846 14.9415 0 13.6431 0ZM14.4369 3.15077L9.59385 8C9.36 8.23385 9.23077 8.54154 9.23077 8.87385V12.4185C9.23077 12.5785 9.16308 12.7385 9.05231 12.8554L7.20615 14.7015C7.05846 14.8431 6.76923 14.72 6.76923 14.5169V8.87385C6.76923 8.54769 6.64 8.23385 6.40615 8.00615L1.56308 3.15077C1.35385 2.94154 1.23077 2.65231 1.23077 2.35692C1.23077 1.73538 1.73538 1.23077 2.35692 1.23077H13.6431C14.2646 1.23077 14.7692 1.73538 14.7692 2.35692C14.7692 2.65231 14.6462 2.94154 14.4369 3.15077Z" fill="#737373"/>
                                    </svg>
                                    <span>Filters</span>
                                </button>
                                {showHistoryFilters && (
                                    <div className={styles.historyFiltersDropdown}>
                                        <div className={styles.historyFiltersDropdownDate}>
                                            <label htmlFor="historyDateFilter" className={styles.historyFiltersDateLabel}>Date</label>
                                            <div className={styles.historyFiltersDateInputBox}>
                                                <input
                                                    id="historyDateFilter"
                                                    type="date"
                                                    className={styles.historyHeaderDateInput}
                                                    value={dateFilter}
                                                    onChange={(e) => handleDateFilter(e.target.value)}
                                                />
                                                {dateFilter && (
                                                    <button type="button" className={styles.historyClearDateBtn} onClick={() => setDateFilter('')} aria-label="Clear date">×</button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className={styles.historySortWrap} ref={historySortRef}>
                                <button
                                    type="button"
                                    className={styles.historySortBtn}
                                    onClick={() => setShowHistorySortDropdown((p) => !p)}
                                    aria-expanded={showHistorySortDropdown}
                                    aria-haspopup="listbox"
                                >
                                    <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M20.7733 11.2458C21.4378 10.5813 22.4471 11.5906 21.7826 12.2551L16.8374 17.2002C16.5753 17.4586 16.1264 17.4707 15.8402 17.1882L10.9081 12.2551C10.2436 11.5906 11.2529 10.5813 11.9174 11.2458L15.632 14.9605V0.714046C15.632 -0.225548 17.0596 -0.225548 17.0596 0.714046V14.9605L20.7733 11.2458ZM5.37079 0.0597137L5.16261 0.19819L0.217439 5.14241C-0.447071 5.80692 0.562233 6.81622 1.22674 6.15171L4.94139 2.43707V16.6835C4.94139 17.6231 6.36891 17.6231 6.36891 16.6835L6.36798 2.43707L10.0826 6.15171C10.7471 6.81622 11.7564 5.80692 11.0919 5.14241L6.15985 0.209373C5.9526 0.00583914 5.64587 -0.0601718 5.37079 0.0597137Z" fill="#737373"/>
                                    </svg>
                                    <span className={styles.historySortBtnLabel}>Sort By :</span>
                                    <span className={styles.historySortBtnValue}>{historySortOrder === 'newest' ? 'Newest First' : 'Oldest First'}</span>
                                    <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M1.7625 0L7.5 5.56275L13.2375 0L15 1.71255L7.5 9L0 1.71255L1.7625 0Z" fill="#737373"/>
                                    </svg>
                                </button>
                                {showHistorySortDropdown && (
                                    <div className={styles.historySortDropdown} role="listbox">
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={historySortOrder === 'newest'}
                                            className={styles.historySortOption}
                                            onClick={() => { setHistorySortOrder('newest'); setShowHistorySortDropdown(false); }}
                                        >
                                            Newest First
                                        </button>
                                        <button
                                            type="button"
                                            role="option"
                                            aria-selected={historySortOrder === 'oldest'}
                                            className={styles.historySortOption}
                                            onClick={() => { setHistorySortOrder('oldest'); setShowHistorySortDropdown(false); }}
                                        >
                                            Oldest First
                                        </button>
                                    </div>
                                )}
                            </div>
                            {/* <button
                                type="button"
                                className={styles.historyHeaderActionButton}
                                onClick={fetchDealCheckerHistory}
                                disabled={historyLoading}
                            >
                                <div className={styles.historyHeaderActionButtonInner}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C9.84871 2 11.501 2.78343 12.614 4H11V5H14V2H13V3.0918C11.7092 1.79227 9.93374 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8H14Z" fill="#737373"/>
                                    </svg>
                                    <span className={styles.historyHeaderActionButtonLabel}>{historyLoading ? 'Loading...' : 'Refresh'}</span>
                                </div>
                            </button> */}
                            {!selectedHistoryBatchId && !historyLoading && !historyError && getFilteredBatches().length > 0 && (
                                <button
                                    type="button"
                                    className={styles.historyHeaderDownloadButton}
                                    onClick={handleDownloadExcel}
                                >
                                    <span className={styles.historyHeaderDownloadLabel}>
                                        Download  {(() => {
                                            const successCount = getFilteredHistoryDataAll().filter(item => item.status === 'SUCCESS').length;
                                            return `(${successCount})`;
                                        })()}
                                    </span>
                                </button>
                            )}
                        </div>
                    </div>
                    )}

                            {/* Batch summary */}
                            {!selectedHistoryBatchId && !historyLoading && !historyError && getFilteredBatches().length > 0 && (
                                <div className={styles.historyBatchSummary}>
                                    <span className={styles.historyBatchSummaryText}>
                                        {getFilteredBatches().length} batch{getFilteredBatches().length !== 1 ? 'es' : ''} · {getFilteredHistoryTotal()} result{getFilteredHistoryTotal() !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            )}

                            {historyLoading ? (
                                <div className={styles.historyLoading}>
                                    <span>Loading history...</span>
                                </div>
                            ) : historyError ? (
                                <div className={styles.errorContainer}>
                                    <span className={styles.errorText}>{historyError}</span>
                                    <button 
                                        className={styles.retryButton}
                                        onClick={fetchDealCheckerHistory}
                                    >
                                        Retry
                                    </button>
                                </div>
                            ) : getFilteredBatches().length === 0 ? (
                        <div className={styles.historyNoData}>
                            No history data found. Start checking ASINs to see results here.
                        </div>
                            ) : selectedHistoryBatchId ? (
                                (() => {
                                    const batch = getFilteredBatches().find(b => b.task_id === selectedHistoryBatchId);
                                    if (!batch) return null;
                                    const asins = batch.results.map(r => r.asin).filter(Boolean);
                                    const asinRange = asins.length ? (asins.length === 1 ? asins[0] : `${asins[0]} - ${asins[asins.length - 1]}`) : '—';
                                    const renderPrimeDelivery = (primeDelivery, standardDelivery) => {
                                        const text = primeDelivery || '-';
                                        if (text === '-') return text;
                                        const match = text.match(/^(.*?)(Order within[^.]*\.?)\s*$/i);
                                        if (match) {
                                            return <><span>{match[1].trim()}. </span><span className={styles.validationOrderWithin}>{match[2]}</span></>;
                                        }
                                        return text;
                                    };
                                    return (
                                        <div className={styles.validationPage}>
                                            <div className={styles.validationHeader}>
                                                <div className={styles.validationHeaderTop}>
                                                    <button type="button" onClick={() => setSelectedHistoryBatchId(null)} className={styles.validationBackBtn} aria-label="Back">
                                                    <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M25 13.75H9.7875L16.775 6.7625L15 5L5 15L15 25L16.7625 23.2375L9.7875 16.25H25V13.75Z" fill="#505050"/>
                                                    </svg>
                                                    </button>
                                                    <h1 className={styles.validationTitle}>Deal Tag Validation</h1>
                                                </div>
                                                <div className={styles.validationAsinBadge}>{asinRange}</div>
                                                <div className={styles.validationDate}>{formatBatchDateLong(batch.date)}</div>
                                            </div>
                                            <div className={styles.validationSearchRow}>
                                                <div className={styles.historySearchBarBox}>
                                                    <div className={styles.historySearchBarInner}>
                                                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M10.421 0C14.5954 0 18 3.40462 18 7.57899C18 11.7534 14.5954 15.158 10.421 15.158C8.67422 15.158 7.06155 14.5613 5.77761 13.5621L1.61719 17.7224C1.24712 18.0925 0.647591 18.0925 0.277544 17.7224C-0.0925028 17.3524 -0.0925265 16.7519 0.277544 16.3828L4.43796 12.2234C3.43876 10.9383 2.84203 9.32678 2.84203 7.58006C2.84203 3.40569 6.24668 0.00106589 10.4211 0.00106589L10.421 0ZM10.421 1.89475C7.27068 1.89475 4.73667 4.42874 4.73667 7.57899C4.73667 10.7292 7.27068 13.2632 10.421 13.2632C13.5712 13.2632 16.1052 10.7292 16.1052 7.57899C16.1052 4.42874 13.5712 1.89475 10.421 1.89475Z" fill="#D2D2D2"/>
                                                        </svg>
                                                        <input
                                                            id="validationSearch"
                                                            type="text"
                                                            className={`${styles.historySearchBarInput} ${validationSearch ? styles.historySearchBarInputFilled : ''}`}
                                                            value={validationSearch}
                                                            onChange={(e) => setValidationSearch(e.target.value)}
                                                            placeholder="Search for ASIN and SKU-IDs"
                                                        />
                                                        {validationSearch && (
                                                            <button type="button" onClick={() => setValidationSearch('')} className={styles.historyClearSearchBtn} aria-label="Clear search">
                                                                <FaTimes className={styles.historyClearSearchBtnIcon} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className={styles.validationTotalDealsWrap}>
                                                    <div className={styles.validationTotalDeals}>
                                                        <span className={styles.validationTotalDealsLabel}>Total Active Deals: </span>
                                                        <span className={styles.validationTotalDealsValue}>{validationTotalActiveDeals}</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className={styles.validationDownloadBtn}
                                                        onClick={handleDownloadValidationBatchExcel}
                                                    >
                                                        <span>Download</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className={styles.validationFilters}>
                                                <div className={styles.validationFiltersLeft}>
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M13.6431 0H2.35692C1.05846 0 0 1.05846 0 2.35692C0 2.98462 0.246154 3.57538 0.689231 4.02462L5.53846 8.87385V14.5169C5.53846 15.3354 6.20308 16 7.02154 16C7.41538 16 7.79692 15.84 8.07385 15.5631L9.92 13.7169C10.2708 13.3662 10.4615 12.9046 10.4615 12.4123V8.86769L15.3108 4.01846C15.7538 3.57538 16 2.98462 16 2.35692C16 1.05846 14.9415 0 13.6431 0ZM14.4369 3.15077L9.59385 8C9.36 8.23385 9.23077 8.54154 9.23077 8.87385V12.4185C9.23077 12.5785 9.16308 12.7385 9.05231 12.8554L7.20615 14.7015C7.05846 14.8431 6.76923 14.72 6.76923 14.5169V8.87385C6.76923 8.54769 6.64 8.23385 6.40615 8.00615L1.56308 3.15077C1.35385 2.94154 1.23077 2.65231 1.23077 2.35692C1.23077 1.73538 1.73538 1.23077 2.35692 1.23077H13.6431C14.2646 1.23077 14.7692 1.73538 14.7692 2.35692C14.7692 2.65231 14.6462 2.94154 14.4369 3.15077Z" fill="#737373"/>
                                                    </svg>
                                                    <span className={styles.validationFiltersLabel}>Filters:</span>
                                                    <div className={styles.validationTabs}>
                                                        <button type="button" className={productStatusFilter === 'all' ? styles.validationTabActive : styles.validationTab} onClick={() => setProductStatusFilter('all')}>All Products</button>
                                                        <button type="button" className={productStatusFilter === 'with_deals' ? styles.validationTabActive : styles.validationTab} onClick={() => setProductStatusFilter('with_deals')}>With Deals</button>
                                                        <button type="button" className={productStatusFilter === 'no_deals' ? styles.validationTabActive : styles.validationTab} onClick={() => setProductStatusFilter('no_deals')}>No Deals</button>
                                                    </div>
                                                    <span className={styles.validationFiltersDivider}>|</span>
                                                    <div className={styles.validationSelectWrap}>
                                                        <select
                                                            className={styles.validationDropdownSelect}
                                                            value={validationDealType}
                                                            onChange={(e) => setValidationDealType(e.target.value)}
                                                        >
                                                            <option value="">Deal type</option>
                                                            {validationDealTypeOptions.map((opt) => (
                                                                <option key={opt} value={opt}>{opt}</option>
                                                            ))}
                                                        </select>
                                                        <span className={styles.validationDropdownArrow} aria-hidden>
                                                            <svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M1.88 0L8 5.9336L14.12 0L16 1.82672L8 9.6L0 1.82672L1.88 0Z" fill="#A4A4A4"/>
                                                            </svg>
                                                        </span>
                                                    </div>
                                                    {/* <span className={styles.validationFiltersDivider}>|</span>
                                                    <div className={styles.validationDropdown}>Product Category <span className={styles.validationDropdownArrow}><svg width="16" height="10" viewBox="0 0 16 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1.88 0L8 5.9336L14.12 0L16 1.82672L8 9.6L0 1.82672L1.88 0Z" fill="#A4A4A4"/></svg></span></div> */}
                                                </div>
                                                <button type="button" className={styles.validationClearFilters} onClick={() => { setValidationSearch(''); setProductStatusFilter('all'); setValidationDealType(''); }}>Clear all filters</button>
                                            </div>
                                            <div className={styles.validationTableCard}>
                                                <div className={styles.validationResultsSummary}>
                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <g opacity="0.7">
                                                    <path d="M11.6985 9.62219C7.84504 8.752 7.24815 8.1551 6.37799 4.30155C6.33812 4.12542 6.18135 4 6.00019 4C5.81904 4 5.66227 4.12542 5.6224 4.30155C4.75185 8.1551 4.15535 8.752 0.301926 9.62219C0.125415 9.66245 0 9.81884 0 10C0 10.1812 0.125415 10.3375 0.301926 10.3778C4.15535 11.2484 4.75185 11.8453 5.6224 15.6985C5.66227 15.8746 5.81904 16 6.00019 16C6.18135 16 6.33812 15.8746 6.37799 15.6985C7.24854 11.8453 7.84504 11.2484 11.6985 10.3778C11.875 10.3375 12 10.1812 12 10C12 9.81884 11.8746 9.66245 11.6985 9.62219Z" fill="#6794C5"/>
                                                    <path d="M15.6965 3.11959C13.635 2.65407 13.3455 2.36463 12.88 0.303467C12.8395 0.125828 12.6821 0 12.4998 0C12.3175 0 12.1601 0.125828 12.1196 0.303467C11.6541 2.36463 11.3646 2.65407 9.30347 3.11959C9.12583 3.16011 9 3.31749 9 3.49981C9 3.68212 9.12583 3.8395 9.30347 3.88002C11.3646 4.34554 11.6541 4.63498 12.1196 6.69653C12.1601 6.87378 12.3175 7 12.4998 7C12.6821 7 12.8395 6.87378 12.88 6.69653C13.3455 4.63498 13.635 4.34554 15.6965 3.88002C15.8738 3.8395 16 3.68212 16 3.49981C16 3.31749 15.8738 3.16011 15.6965 3.11959Z" fill="#6794C5"/>
                                                    </g>
                                                    </svg>
                                                    <span className={styles.validationResultsSummaryText}> Showing Results for {validationGroupedByAsin.length} ASINs</span>
                                                </div>
                                                <div className={styles.validationTableHead}>
                                                    <div className={styles.validationThProduct}>Product & ASIN</div>
                                                    <div className={styles.validationThPincode}>Pincode</div>
                                                    <div className={styles.validationThDeal}>Deal Badge</div>
                                                    <div className={styles.validationThCoupon}>Coupon</div>
                                                    <div className={styles.validationThDealPrice}>Deal Selling Price</div>
                                                    <div className={styles.validationThBuybox}>Buy Box Winner</div>
                                                    <div className={styles.validationThDelivery}>Delivery</div>
                                                    <div className={styles.validationThPrime}>Prime Delivery</div>
                                                </div>
                                                <div className={styles.validationTableBody}>
                                                    {validationGroupedByAsin.length === 0 ? (
                                                        <div className={styles.validationNoResults}>No ASINs match your filters. Try clearing search or changing the product filter.</div>
                                                    ) : validationGroupedByAsin.map((group) => (
                                                        <div key={group.asin} className={styles.validationAsinCard}>
                                                            <div className={styles.validationProductCol}>
                                                                <div className={styles.validationProductThumb}>
                                                                    <img
                                                                        src={group.imageUrl}
                                                                        alt={group.title || group.asin || 'Product image'}
                                                                        onError={(e) => {
                                                                            e.target.onerror = null;
                                                                            e.target.src = getAsinThumbnailUrl(group.asin);
                                                                        }}
                                                                    />
                                                                </div>
                                                                <div className={styles.validationProductInfo}>
                                                                    <div className={styles.validationProductTitle}>{group.title}</div>
                                                                    <div className={styles.validationAsinBadgeSmall}>{group.asin}</div>
                                                                </div>
                                                            </div>
                                                            <div className={styles.validationRowsCol}>
                                                                {group.rows.map((row, idx) => (
                                                                    <div key={`${group.asin}-${row.zipcode}-${idx}`} className={styles.validationRow}>
                                                                        <div className={styles.validationCellPincode}>{row.zipcode}</div>
                                                                        <div className={styles.validationCellDeal}>
                                                                            {row.deal_present && row.deal_badge && row.deal_badge !== 'Not applicable' && row.deal_badge !== 'N/A' ? (
                                                                                <span className={styles.validationDealBadge}>{row.deal_badge}</span>
                                                                            ) : (
                                                                                <span className={styles.validationDealNA}>Not Applicable</span>
                                                                            )}
                                                                        </div>
                                                                        <div className={styles.validationCellCoupon}>
                                                                            {(row.has_coupon && row.coupon_text && row.coupon_text !== 'N/A') ? (
                                                                                <span className={styles.validationDealBadge}>{formatCouponText(row.coupon_text)}</span>
                                                                            ) : '-'}
                                                                        </div>
                                                                        <div className={styles.validationCellDealPrice}>{row.current_price && row.current_price !== 'N/A' ? row.current_price : '-'}</div>
                                                                        <div className={styles.validationCellBuybox}>{row.sold_by && row.sold_by !== 'N/A' ? row.sold_by : '-'}</div>
                                                                        <div className={styles.validationCellDelivery}>{row.standardDelivery || '-'}</div>
                                                                        <div className={styles.validationCellPrime}>{renderPrimeDelivery(row.primeDelivery, row.standardDelivery)}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()
                            ) : (
                                <>
                                    <div className={styles.historyBatchGrid}>
                                        {getFilteredBatches().map((batch) => {
                                            const asins = batch.results.map(r => r.asin).filter(Boolean);
                                            const productCount = new Set(asins).size; // number of ASINs requested
                                            const activeDeals = batch.results.filter(r => r.deal_present).length;
                                            const asinRange = asins.length ? (asins.length === 1 ? asins[0] : `${asins[0]} - ${asins[asins.length - 1]}`) : '—';
                                            // Deduplicate by ASIN so each product image shows only once in the grid
                                            const seenAsins = new Set();
                                            const uniqueResults = batch.results.filter(r => {
                                                if (seenAsins.has(r.asin)) return false;
                                                seenAsins.add(r.asin);
                                                return true;
                                            });
                                            const count = uniqueResults.length;
                                            const { displayCount, gridCols } = getThumbnailDisplay(count);
                                            const thumbsToShow = uniqueResults.slice(0, displayCount);
                                            const gridRows = Math.ceil(displayCount / gridCols);
                                            return (
                                                <div
                                                    key={batch.task_id}
                                                    className={styles.batchCardStack}
                                                    onClick={() => { setSelectedHistoryBatchId(batch.task_id); setValidationSearch(''); setProductStatusFilter('all'); setValidationDealType(''); }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedHistoryBatchId(batch.task_id); setValidationSearch(''); setProductStatusFilter('all'); setValidationDealType(''); } }}
                                                    aria-label={`View batch ${asinRange}`}
                                                >
                                                    <div className={`${styles.batchCardStackBehind} ${styles.batchCardStackBehindFirst}`} aria-hidden="true" />
                                                    <div className={`${styles.batchCardStackBehind} ${styles.batchCardStackBehindSecond}`} aria-hidden="true" />
                                                    <div className={styles.batchCardMain}>
                                                        <div
                                                            className={styles.batchCardThumbGrid}
                                                            style={{
                                                                gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                                                                gridTemplateRows: `repeat(${gridRows}, 1fr)`,
                                                            }}
                                                        >
                                                            {thumbsToShow.map((item, i) => (
                                                                <div key={`${item.asin}-${item.zipcode || ''}-${i}`} className={styles.batchCardThumb}>
                                                                    <img
                                                                        src={item.main_image || getAsinThumbnailUrl(item.asin)}
                                                                        alt=""
                                                                        onError={(e) => { e.target.onerror = null; e.target.src = getAsinThumbnailUrl(item.asin); }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <div className={styles.batchCardContent}>
                                                            <div className={styles.batchCardStatsRow}>
                                                                <span className={styles.batchCardProductCount}>{productCount} Product{productCount !== 1 ? 's' : ''}</span>
                                                                <div className={styles.batchCardActiveDeals}>
                                                                    <span className={styles.batchCardActiveDealsDot} />
                                                                    <span>{activeDeals} Active Deal{activeDeals !== 1 ? 's' : ''}</span>
                                                                </div>
                                                            </div>
                                                            <div className={styles.batchCardAsinBadge}>{asinRange}</div>
                                                            <div className={styles.batchCardDateLong}>{formatBatchDateLong(batch.date)}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </div>
            )}

            {/* Overflow Modal */}
            {showOverflowModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3 className={styles.modalTitle}>Too Many ASINs</h3>
                        <p className={styles.modalDescription}>
                            You've pasted <strong>{asinTags.length + overflowAsins.length} ASINs</strong>, but we can only process <strong>100 ASINs</strong> at a time.
                        </p>
                        <p className={styles.modalDescription}>
                            We've added the <strong>first 100 ASINs</strong> to your list. The remaining <strong>{overflowAsins.length} ASINs</strong> were not added.
                        </p>
                        <div className={styles.modalSuggestions}>
                            <p className={styles.suggestionTitle}>What you can do:</p>
                            <ul className={styles.suggestionList}>
                                <li>Process the current 100 ASINs first, then add the remaining ones</li>
                                <li>Remove some ASINs from the current list to make room for others</li>
                                <li>Split your ASINs into smaller batches of 100 or fewer</li>
                            </ul>
                        </div>
                        <div className={styles.modalActions}>
                            <button
                                onClick={() => {
                                    setShowOverflowModal(false);
                                    setOverflowAsins([]);
                                }}
                                className={styles.confirmButton}
                            >
                                Got it
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Success Popup for Async Processing */}
            <QueueNotificationPopup
                show={showSuccessPopup}
                onClose={handleCloseSuccessPopup}
                onResetFiles={resetFormFields}
                title="Deal Check Started"
            />

            {/* Buy Credits Modal */}
            <TokenPurchaseModal
                show={showTokenPurchaseModal}
                onClose={handleCloseTokenPurchaseModal}
            />

            {/* Zipcode Modal */}
            {showZipcodeModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3 className={styles.modalTitle}>Select Zipcode</h3>
                        <div className={styles.modalDescription}>
                            Select one or more zipcodes to check deal tags for different regions.
                        </div>
                        <div className={styles.zipcodeList}>
                            {[
                                { region: 'North', city: 'Delhi', zipcode: '110001' },
                                { region: 'South', city: 'Bangalore', zipcode: '560001' },
                                { region: 'East', city: 'Kolkata', zipcode: '700001' },
                                { region: 'West', city: 'Mumbai', zipcode: '400001' },
                                { region: 'South', city: 'Hyderabad', zipcode: '500001' },
                                { region: 'South', city: 'Chennai', zipcode: '600001' }
                            ].map((item) => (
                                <label key={item.zipcode} className={styles.zipcodeItem}>
                                    <input
                                        type="checkbox"
                                        checked={selectedZipcodes.includes(item.zipcode)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedZipcodes([...selectedZipcodes, item.zipcode]);
                                            } else {
                                                setSelectedZipcodes(selectedZipcodes.filter(z => z !== item.zipcode));
                                            }
                                        }}
                                        className={styles.zipcodeCheckbox}
                                    />
                                    <span className={styles.zipcodeLabel}>
                                        {item.region} - {item.city} - {item.zipcode}
                                    </span>
                                </label>
                            ))}
                        </div>
                        <div className={styles.modalActions}>
                            <button
                                onClick={() => setShowZipcodeModal(false)}
                                className={styles.confirmButton}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading && <LoadingOverlay />}
        </div>
    );
};

export default CheckDealTags;
